import type { SmartAssistantSettingValues } from "@sukha/shared/domain/smartAssistantSettings";

/** What an assistant line is, for the member's switches and the per-type cooldowns. */
export type AudioMessageType =
  | "phoneNudge"
  | "phoneNudge:distracted"
  | "phoneNudge:focused"
  | "websiteNudge"
  | "encouragement"
  | "encouragement:break"
  | "encouragement:focus"
  | "encouragement:break_nudge"
  | "encouragement:focus_nudge"
  | "encouragement:session_start"
  | "encouragement:session_end"
  | "taskHelp"
  | "meetingReminder"
  | "onboardingNarration"
  /** No member switch: only audio the member starts with a tap (guarded by spokenLineSwitches.guard.test.ts). */
  | "general";

/** The settings the gate reads: the resolved SmartAssistant switches (`@sukha/shared`). */
export type AssistantMessageSettings = SmartAssistantSettingValues;

/**
 * The phone nudge's types. They answer to "Phone Nudges" only, and play
 * whatever the assistant-audio switch says (TX-5182).
 */
export function isPhoneNudgeType(type: AudioMessageType): boolean {
  return type === "phoneNudge" || type.startsWith("phoneNudge:");
}

/**
 * Whether the member's switch for this kind of line lets it play. `undefined`
 * settings (not loaded yet) let everything through, as before.
 */
export function isMessageTypeEnabled(
  type: AudioMessageType,
  settings: AssistantMessageSettings | undefined
): boolean {
  if (!settings) return true;
  switch (type) {
    // "Phone Nudges" and "Website Nudges" are separate switches (TX-5182).
    case "phoneNudge":
    case "phoneNudge:distracted":
    case "phoneNudge:focused":
      return settings.phoneNudgeEnabled;
    // "Website Nudges" is its own column since TX-5183; `phoneAndWebSiteNudge`
    // is deprecated and decides nothing.
    case "websiteNudge":
      return settings.websiteNudgeEnabled;
    // "Spoken Words of Encouragement": the random lines only (TX-5253).
    case "encouragement":
      return settings.wordsOfEncouragement;
    // "Spoken Session Transitions" (TX-5253): session start/end, break start,
    // back to focus, and the "break / focus in N minutes" warnings.
    case "encouragement:break":
    case "encouragement:focus":
    // The N-minute warnings have cues of their own, so a warning never holds
    // the cooldown of the transition line that follows it.
    case "encouragement:break_nudge":
    case "encouragement:focus_nudge":
    case "encouragement:session_start":
    case "encouragement:session_end":
      return settings.spokenSessionTransitions;
    // "Spoken Onboarding Narration" (TX-5253).
    case "onboardingNarration":
      return settings.onboardingNarrationEnabled;
    // "Spoken Task Nudges" (TX-5251), still stored as `generalTaskHelp`: the
    // spoken task-timebox nudge only. Its toast is not gated by any setting.
    case "taskHelp":
      return settings.generalTaskHelp;
    // Resolved: a NULL column is on (TX-5177).
    case "meetingReminder":
      return settings.meetingRemindersEnabled;
    case "general":
    default:
      return true;
  }
}
